export type SearchResult =
    | { status: "ok"; start: number; end: number }
    | { status: "not_found" }
    | { status: "ambiguous"; count: number };

/**
 * Find the single occurrence of `needle` in `content`. Returns its
 * [start, end) character offsets (end exclusive) when exactly one match
 * exists; otherwise reports not_found or ambiguous (with the match count).
 */
export function findUniqueRange(content: string, needle: string): SearchResult {
    if (!needle) return { status: "not_found" };
    const first = content.indexOf(needle);
    if (first === -1) return { status: "not_found" };
    const second = content.indexOf(needle, first + needle.length);
    if (second !== -1) {
        let count = 2;
        let idx = second;
        while ((idx = content.indexOf(needle, idx + needle.length)) !== -1) count++;
        return { status: "ambiguous", count };
    }
    return { status: "ok", start: first, end: first + needle.length };
}

/**
 * Convert a 0-based character offset into a 1-based Monaco position. Column
 * counts UTF-16 code units from the line start, matching Monaco's model
 * coordinates. Offsets are clamped into [0, content.length].
 */
export function offsetToPosition(
    content: string,
    offset: number
): { lineNumber: number; column: number } {
    const clamped = Math.max(0, Math.min(offset, content.length));
    let line = 1;
    let lineStart = 0;
    for (let i = 0; i < clamped; i++) {
        if (content.charCodeAt(i) === 10 /* \n */) {
            line++;
            lineStart = i + 1;
        }
    }
    return { lineNumber: line, column: clamped - lineStart + 1 };
}
